/**
 * BookingPage — Public booking page for leads.
 * Calendly-style layout: month calendar + time slot selection on one page.
 * No authentication required.
 */

import { useState, useEffect, useCallback } from 'react';
import { Clock, Phone, ChevronLeft, ChevronRight, Check, User, Mail, Video } from 'lucide-react';

interface Service {
  id: string;
  name: string;
  description: string;
  duration_minutes: number;
  session_methods: string[];
}

interface Slot {
  start_time: string;
  end_time: string;
  coach_id: string;
}

interface DaySlots {
  date: string;
  slots: Slot[];
}

export function BookingPage() {
  // Flow state
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Data
  const [services, setServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [slotResults, setSlotResults] = useState<DaySlots[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());

  // Calendar state
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  // Form state
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch public services
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/booking/public/services');
        const data = await res.json();
        setServices(data.services || []);
        // Auto-select if only one service
        if (data.services?.length === 1) setSelectedService(data.services[0]);
      } catch { /* ignore */ }
      finally { setLoadingServices(false); }
    })();
  }, []);

  // Fetch slots for the displayed month when service is selected
  const fetchMonthSlots = useCallback(async () => {
    if (!selectedService) return;
    setLoadingSlots(true);
    try {
      const startDate = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(calYear, calMonth + 1, 0).getDate();
      const endDate = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${lastDay}`;

      const res = await fetch(`/api/booking/public/slots?service_id=${selectedService.id}&start_date=${startDate}&end_date=${endDate}`);
      const data = await res.json();
      const results = data.results || [];
      setSlotResults(results);

      // Build set of available dates
      const dates = new Set<string>();
      for (const day of results) {
        if (day.slots.length > 0) dates.add(day.date);
      }
      setAvailableDates(dates);
    } catch { /* ignore */ }
    finally { setLoadingSlots(false); }
  }, [selectedService, calMonth, calYear]);

  useEffect(() => {
    if (selectedService) fetchMonthSlots();
  }, [selectedService, fetchMonthSlots]);

  // Get slots for selected date
  const slotsForSelectedDate = selectedDate
    ? slotResults.find(d => d.date === selectedDate)?.slots || []
    : [];

  // Calendar helpers
  const generateCalendarDays = () => {
    const firstDay = new Date(calYear, calMonth, 1);
    const lastDay = new Date(calYear, calMonth + 1, 0);
    const startPad = firstDay.getDay();
    const days: (number | null)[] = [];

    for (let i = 0; i < startPad; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
    return days;
  };

  const isDateAvailable = (day: number) => {
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return availableDates.has(dateStr);
  };

  const isDatePast = (day: number) => {
    const date = new Date(calYear, calMonth, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isDateSelected = (day: number) => {
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateStr === selectedDate;
  };

  const selectDate = (day: number) => {
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setShowForm(false);
  };

  const prevMonth = () => {
    const now = new Date();
    if (calYear === now.getFullYear() && calMonth <= now.getMonth()) return;
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const formatTime = (iso: string) => {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatSelectedDate = () => {
    if (!selectedDate) return '';
    const d = new Date(selectedDate + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const monthLabel = new Date(calYear, calMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const handleSelectSlot = (slot: Slot) => {
    setSelectedSlot(slot);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!selectedService || !selectedSlot || !form.name || !form.email) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch('/api/booking/public/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: selectedService.id,
          slot_start: selectedSlot.start_time,
          slot_end: selectedSlot.end_time,
          name: form.name,
          email: form.email,
          phone: form.phone,
          notes: form.notes
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSubmitted(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (submitted) {
    return (
      <div className="booking-page">
        <div className="booking-card success-card">
          <div className="success-icon"><Check size={48} /></div>
          <h2>You're All Set</h2>
          <p>We've received your request for a <strong>{selectedService?.name}</strong>.</p>
          <p><strong>{formatSelectedDate()}</strong> at <strong>{formatTime(selectedSlot!.start_time)}</strong></p>
          <p className="success-note">You'll receive a confirmation email at <strong>{form.email}</strong> shortly.</p>
          <a href="/" className="btn btn-primary">Back to Home</a>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="booking-page">
      {/* Service selection (if multiple) */}
      {!selectedService && (
        <div className="booking-card">
          <h1>Book an Appointment</h1>
          <p className="subtitle">Select a service to get started.</p>
          {loadingServices ? <p className="loading-text">Loading...</p> : (
            <div className="service-list">
              {services.map(svc => (
                <button key={svc.id} className="service-option" onClick={() => setSelectedService(svc)}>
                  <div className="svc-name">{svc.name}</div>
                  {svc.description && <div className="svc-desc">{svc.description}</div>}
                  <div className="svc-meta">
                    <Clock size={14} /> {svc.duration_minutes} min
                    {svc.session_methods.includes('phone') && <><Phone size={14} /> Phone</>}
                    {svc.session_methods.includes('virtual') && <><Video size={14} /> Virtual</>}
                  </div>
                </button>
              ))}
              {services.length === 0 && <p className="empty-text">No services available at this time.</p>}
            </div>
          )}
        </div>
      )}

      {/* Calendar + time picker */}
      {selectedService && !showForm && (
        <div className="booking-card">
          {services.length > 1 && (
            <button className="back-link" onClick={() => { setSelectedService(null); setSelectedDate(null); setSelectedSlot(null); }}>
              <ChevronLeft size={16} /> Back to services
            </button>
          )}
          <h1>{selectedService.name}</h1>
          <p className="subtitle"><Clock size={14} /> {selectedService.duration_minutes} minutes</p>

          <div className="picker-layout">
            {/* Calendar */}
            <div className="calendar-panel">
              <div className="cal-header">
                <button onClick={prevMonth} className="cal-nav"><ChevronLeft size={18} /></button>
                <span className="cal-month">{monthLabel}</span>
                <button onClick={nextMonth} className="cal-nav"><ChevronRight size={18} /></button>
              </div>
              <div className="cal-grid">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                  <div key={d} className="cal-dow">{d}</div>
                ))}
                {generateCalendarDays().map((day, i) => (
                  <div key={i} className="cal-cell">
                    {day && (
                      <button
                        className={`cal-day ${isDateAvailable(day) ? 'available' : 'unavailable'} ${isDatePast(day) ? 'past' : ''} ${isDateSelected(day) ? 'selected' : ''}`}
                        onClick={() => isDateAvailable(day) && !isDatePast(day) && selectDate(day)}
                        disabled={!isDateAvailable(day) || isDatePast(day)}
                      >
                        {day}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {loadingSlots && <p className="loading-text">Loading availability...</p>}
            </div>

            {/* Time slots */}
            <div className="slots-panel">
              {!selectedDate ? (
                <div className="slots-placeholder">
                  <p>Select a date to see available times</p>
                </div>
              ) : slotsForSelectedDate.length === 0 ? (
                <div className="slots-placeholder">
                  <p>No available times on this date</p>
                </div>
              ) : (
                <>
                  <div className="slots-header">{formatSelectedDate()}</div>
                  <div className="slots-list">
                    {slotsForSelectedDate.map((slot, i) => (
                      <button
                        key={i}
                        className={`slot-btn ${selectedSlot?.start_time === slot.start_time ? 'selected' : ''}`}
                        onClick={() => handleSelectSlot(slot)}
                      >
                        {formatTime(slot.start_time)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contact form */}
      {showForm && selectedSlot && (
        <div className="booking-card">
          <button className="back-link" onClick={() => setShowForm(false)}>
            <ChevronLeft size={16} /> Back to times
          </button>
          <h1>Your Information</h1>

          <div className="booking-summary-bar">
            <span>{selectedService?.name}</span>
            <span>{formatSelectedDate()}</span>
            <span>{formatTime(selectedSlot.start_time)}</span>
          </div>

          {error && <div className="booking-error">{error}</div>}

          <div className="contact-form">
            <div className="form-group">
              <label><User size={14} /> Full Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Your name" />
            </div>
            <div className="form-group">
              <label><Mail size={14} /> Email *</label>
              <input type="email" value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} placeholder="your@email.com" />
            </div>
            <div className="form-group">
              <label><Phone size={14} /> Phone</label>
              <input type="tel" value={form.phone} onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="(555) 123-4567" />
            </div>
            <div className="form-group">
              <label>Anything you'd like us to know? (optional)</label>
              <textarea value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} />
            </div>
            <button className="btn btn-primary submit-btn" onClick={handleSubmit} disabled={submitting || !form.name || !form.email}>
              {submitting ? 'Submitting...' : 'Confirm Booking'}
            </button>
          </div>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .booking-page {
    min-height: 100vh;
    background: var(--bg-secondary);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 40px 20px;
  }

  .booking-card {
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: 16px;
    padding: 36px;
    width: 100%;
    max-width: 820px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  }

  .booking-card h1 {
    color: var(--text-primary);
    font-size: 24px;
    margin: 0 0 4px;
  }

  .subtitle {
    color: var(--text-secondary);
    font-size: 14px;
    margin: 0 0 24px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .back-link {
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    color: var(--text-tertiary);
    cursor: pointer;
    font-size: 13px;
    padding: 0;
    margin-bottom: 16px;
  }
  .back-link:hover { color: var(--text-primary); }

  .loading-text { color: var(--text-tertiary); font-size: 13px; text-align: center; padding: 12px; }
  .empty-text { color: var(--text-tertiary); text-align: center; padding: 40px; font-style: italic; }

  /* Service list */
  .service-list { display: flex; flex-direction: column; gap: 12px; }
  .service-option {
    text-align: left;
    padding: 20px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.15s;
  }
  .service-option:hover { border-color: var(--accent-blue); transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
  .svc-name { font-size: 17px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px; }
  .svc-desc { font-size: 13px; color: var(--text-secondary); margin-bottom: 12px; line-height: 1.5; }
  .svc-meta { display: flex; gap: 12px; font-size: 12px; color: var(--text-tertiary); align-items: center; }
  .svc-meta svg { flex-shrink: 0; }

  /* Picker layout */
  .picker-layout {
    display: flex;
    gap: 32px;
  }

  .calendar-panel { flex: 0 0 280px; }
  .slots-panel { flex: 1; min-width: 0; }

  /* Calendar */
  .cal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  .cal-month { font-weight: 600; font-size: 16px; color: var(--text-primary); }
  .cal-nav {
    background: none;
    border: 1px solid var(--border-secondary);
    border-radius: 8px;
    padding: 4px 8px;
    cursor: pointer;
    color: var(--text-secondary);
    display: flex;
  }
  .cal-nav:hover { color: var(--text-primary); border-color: var(--text-tertiary); }

  .cal-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
  }
  .cal-dow {
    text-align: center;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-tertiary);
    padding: 4px 0;
    text-transform: uppercase;
  }
  .cal-cell {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cal-day {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
    background: none;
    color: var(--text-primary);
  }
  .cal-day.available { color: var(--text-primary); font-weight: 600; }
  .cal-day.available:hover { background: rgba(59, 130, 246, 0.1); color: var(--accent-blue); }
  .cal-day.unavailable { color: var(--text-tertiary); opacity: 0.4; cursor: default; }
  .cal-day.past { color: var(--text-tertiary); opacity: 0.3; cursor: default; }
  .cal-day.selected {
    background: var(--accent-blue);
    color: white;
  }

  /* Slots */
  .slots-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    color: var(--text-tertiary);
    font-size: 14px;
  }
  .slots-header {
    font-weight: 600;
    font-size: 14px;
    color: var(--text-primary);
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--border-secondary);
  }
  .slots-list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    max-height: 320px;
    overflow-y: auto;
  }
  .slot-btn {
    padding: 12px 16px;
    border: 1px solid var(--border-secondary);
    border-radius: 8px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
    text-align: center;
  }
  .slot-btn:hover { border-color: var(--accent-blue); background: rgba(59, 130, 246, 0.05); }
  .slot-btn.selected { background: var(--accent-blue); color: white; border-color: var(--accent-blue); }

  /* Contact form */
  .booking-summary-bar {
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    background: rgba(16, 185, 129, 0.08);
    border: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: 10px;
    color: #10b981;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }
  .booking-summary-bar span:not(:last-child)::after {
    content: '\\2022';
    margin-left: 12px;
    color: rgba(16, 185, 129, 0.4);
  }

  .booking-error {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    padding: 10px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
  }

  .contact-form { display: flex; flex-direction: column; gap: 16px; }
  .form-group { display: flex; flex-direction: column; gap: 4px; }
  .form-group label {
    font-size: 13px; font-weight: 600; color: var(--text-secondary);
    display: flex; align-items: center; gap: 6px;
  }
  .form-group input, .form-group textarea {
    padding: 10px 14px;
    border: 1px solid var(--border-secondary);
    border-radius: 8px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    font-size: 15px;
    font-family: inherit;
  }
  .form-group input:focus, .form-group textarea:focus {
    outline: none; border-color: var(--accent-blue);
  }
  .submit-btn { margin-top: 8px; padding: 14px; font-size: 16px; justify-content: center; width: 100%; }

  /* Success */
  .success-card { text-align: center; max-width: 480px; }
  .success-icon {
    width: 80px; height: 80px; border-radius: 50%;
    background: rgba(16, 185, 129, 0.1); color: #10b981;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 24px;
  }
  .success-card h2 { color: var(--text-primary); margin: 0 0 12px; }
  .success-card p { color: var(--text-secondary); margin: 0 0 8px; font-size: 15px; }
  .success-note { margin-top: 16px; font-size: 13px; color: var(--text-tertiary); }
  .success-card .btn { margin-top: 24px; text-decoration: none; display: inline-flex; }

  .btn {
    padding: 10px 20px; border-radius: 8px; font-size: 15px; font-weight: 600;
    cursor: pointer; border: none; transition: all 0.15s;
    display: flex; align-items: center; gap: 6px;
  }
  .btn-primary { background: var(--accent-blue); color: white; }
  .btn-primary:hover { opacity: 0.9; }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Mobile */
  @media (max-width: 600px) {
    .booking-page { padding: 16px 12px; }
    .booking-card { padding: 24px 20px; border-radius: 12px; }
    .picker-layout { flex-direction: column; gap: 24px; }
    .calendar-panel { flex: none; }
    .slots-list { max-height: none; grid-template-columns: repeat(2, 1fr); }
    .booking-summary-bar { flex-direction: column; gap: 4px; }
    .booking-summary-bar span::after { display: none; }
  }
`;
